import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import type { SanityAssetSource } from './schemas';
import type { Action } from 'svelte/action';
import { get, writable, type Writable } from 'svelte/store';

type SrcSetOptions = {
  min: number;
  max: number;
  step: number;
};

let defaultSrcsetOptions: SrcSetOptions = {
  min: 420,
  max: 3600,
  step: 240
};

/**
 * Returns a converter function that should be used to convert
 * a single sanity image url builder to an array of builders based
 * on the values of srcsetOptions.
 */
export const toSrcsetUrlBuilders = (
  urlBuilder: ImageUrlBuilder,
  srcsetOptions: SrcSetOptions = defaultSrcsetOptions
) => {
  return (imageAsset: SanityAssetSource) => {
    const builders = [];

    for (let i = srcsetOptions.min; i <= srcsetOptions.max; i += srcsetOptions.step) {
      builders.unshift(urlBuilder.image(imageAsset).width(i).auto('format'));
    }

    return builders;
  };
};

/**
 * Converts sanity image url builders to `src` and `srcset` attributes for later
 * use in `img` components.
 */
export const toImageSrcAttributes = (srcsetOptions: SrcSetOptions = defaultSrcsetOptions) => {
  return (builders: ImageUrlBuilder[]) => {
    const srcsetArr = builders.map((builder, i) => {
      const w = srcsetOptions.max - srcsetOptions.step * i;

      return `${builder.url()} ${w}w`;
    });

    return {
      srcset: srcsetArr.join(', '),
      src: srcsetArr[srcsetArr.length - 1].split(' ')[0]
    };
  };
};

type LazyloadSrcsetOptions = {
  quality: number;
  blur: number;
};

const defaultLazyloadSrcsetOptions = {
  quality: 1,
  blur: 90
};
/**
 * Should be used alongside `useSanityImageLazyload` which produces value
 * of isInView. Use this to lower quality of your images when they are not
 * in the viewport.
 */
export const toLazyloadSrcsetUrlBuilders = (
  isInView: boolean,
  options: LazyloadSrcsetOptions = defaultLazyloadSrcsetOptions
) => {
  return (builders: ImageUrlBuilder[]) => {
    if (!isInView) {
      return builders.map((urlBuilder) => urlBuilder.quality(options.quality).blur(options.blur));
    }

    return builders;
  };
};

type LazyloadOptions = {
  rootMargin: string;
  class: string;
};

const defaultLazyloadOptions = { rootMargin: '200px', class: 'lazy' };

/**
 * Provides a lazyload svelte action that could be attached to `img` node and a store value
 * that could be subscribed to and be notified when the node enters the viewport.
 *
 * Should be used alongside `toLazyloadSrcsetUrlBuilders`.
 */
export const useSanityImageLazyload = (
  options: LazyloadOptions = defaultLazyloadOptions
): {
  isInView: Writable<boolean>;
  lazyload: Action<HTMLElement>;
} => {
  const isInView = writable(false);

  function lazyload(node: HTMLElement) {
    const handleLoaded = () => {
      if (get(isInView)) {
        node.classList.remove(options.class);
      }
    };

    const handleIntesectionEntriesStatusChange = (entries: IntersectionObserverEntry[]) => {
      const entry = entries.find((entry) => entry.target === node);

      if (entry?.isIntersecting) {
        isInView.set(true);
      }
    };

    let observer = new IntersectionObserver(handleIntesectionEntriesStatusChange, {
      rootMargin: options.rootMargin
    });

    observer.observe(node);

    node.addEventListener('load', handleLoaded);

    return {
      destroy() {
        node.removeEventListener('load', handleLoaded);
        observer.unobserve(node);
        observer.disconnect();
      }
    };
  }

  return {
    isInView,
    lazyload
  };
};
